import classNames from 'classnames'
import {
  cloneElement,
  forwardRef,
  Fragment,
  HTMLAttributes,
  isValidElement,
  ReactNode,
} from 'react'

import { IconButton } from '../icon-button/icon-button'
import { Slot } from '../utilities'
import styles from './chat-message.module.scss'
import { useChatMessageContext } from './context'

export type ChatMessageFooterControlsElement = HTMLDivElement
export type ChatMessageFooterControlsProps = HTMLAttributes<ChatMessageFooterControlsElement> & {
  asChild?: boolean
}

export const ChatMessageFooterControls = forwardRef<
  ChatMessageFooterControlsElement,
  ChatMessageFooterControlsProps
>(function ChatMessageFooterControls(props, forwardedRef) {
  const { children, className, asChild, ...rest } = props
  const { loading } = useChatMessageContext()
  const classes = classNames(styles.footerControls, className)

  if (loading) {
    return null
  }

  const Component = asChild ? Slot : 'div'

  return (
    <Component {...rest} className={classes} ref={forwardedRef}>
      {renderChildren(children)}
    </Component>
  )
})

function renderChildren(children: React.ReactNode): ReactNode {
  if (Array.isArray(children)) {
    return children.map((child, index) => {
      const node = renderChildren(child)
      if (!isValidElement(node)) return node
      const key = child.key ?? index
      return cloneElement(node, { key })
    })
  }
  if (!isValidElement(children)) {
    return children
  }
  if (children.type === Fragment) {
    return renderChildren(children.props.children)
  }
  if (children.type === IconButton) {
    return <IconButton {...children.props} size="xs" kind="plain" />
  }
  if (children.props.children) {
    return cloneElement(children, children.props, renderChildren(children.props.children))
  }
  return children
}
